import React, {FC, useState} from "react"
import {H3, H4, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {Checkbox} from "@irati/ui/src/FormItem"
import Button from "@irati/ui/src/Button"
import {FormattedMessage, useIntl} from "@irati/utils/lang"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Link from "@irati/ui/src/Link"
import axios from "axios"
import {useCreateCustomerFile, useDeleteCustomerFile} from "@irati/customer-files/hooks/customerFile"
import customerFileService from "@irati/customer-files/services/customerFile"
import {CompanyNewsTileConfig, FileStatus} from "@irati/types"
import {useCultureHubStore} from "../../../stores/hooks"

interface Props {
  updateContent: (companyNewsTileConfig: CompanyNewsTileConfig) => void
  companyNewsTileConfig: CompanyNewsTileConfig
}

const AcceptTerms: FC<Props> = ({updateContent, companyNewsTileConfig}) => {
  const {formatMessage} = useIntl()
  const {state, dispatch} = useCultureHubStore()
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [createFileMutation] = useCreateCustomerFile()
  const [deleteFileMutation] = useDeleteCustomerFile()

  const pollStatus = async (uuid: string) => {
    dispatch({type: "SET_IS_POOLING", payload: {isPooling: true}})
    const data = await customerFileService.customerFile({uuid}, {fetchPolicy: "network-only"})

    switch (data?.file?.status) {
      case FileStatus.StatusReady:
        dispatch({
          type: "UPLOAD_STATUS",
          payload: {uploadStatus: data.file.status, uploadName: state.acceptedFileVisual?.name}
        })
        updateContent(Object.assign({}, companyNewsTileConfig, {fileId: uuid}))
        dispatch({type: "SET_PDF_ERROR", payload: {isPDFValid: false}})
        dispatch({type: "SET_IS_POOLING", payload: {isPooling: false}})
        break
      case FileStatus.StatusInfected:
        dispatch({
          type: "SET_UPLOAD_ERROR_MSG",
          payload: {uploadErrorMsg: "culturehub.companyNews.modal.uploadFile.errors.status.infected"}
        })
        dispatch({type: "UPLOAD_STATUS", payload: {uploadStatus: data.file.status, uploadName: ""}})
        dispatch({type: "SET_IS_POOLING", payload: {isPooling: false}})
        break
      case FileStatus.StatusFailed:
        dispatch({
          type: "SET_UPLOAD_ERROR_MSG",
          payload: {uploadErrorMsg: "culturehub.companyNews.modal.uploadFile.errors.status.failed"}
        })
        dispatch({type: "UPLOAD_STATUS", payload: {uploadStatus: data.file.status, uploadName: ""}})
        dispatch({type: "SET_IS_POOLING", payload: {isPooling: false}})
        break
      default:
        setTimeout(() => {
          return pollStatus(uuid)
        }, 1000)
    }
  }

  const handleChange = async () => {
    if (!isChecked) {
      setShowError(true)
    } else {
      const formData = new FormData()
      if (state.acceptedFile?.type === "application/pdf") {
        formData.append("file", state.acceptedFile)
      }
      dispatch({
        type: "SET_MODAL_STATE",
        payload: {modalState: undefined}
      })

      try {
        /** Logic to trigger the delete mutation and reset fileId, upload status and name if replacing the file */
        if (companyNewsTileConfig.fileId) {
          if (state.prevFileId) {
            /**
             * This check is to make sure we delete any file from S3 and DB if in edit mode
             * and the user has already created one file and decides to replace it
             */
            deleteFileMutation({
              variables: {uuid: companyNewsTileConfig.fileId}
            })
          } else {
            dispatch({type: "SET_PREV_FILE_ID", payload: {prevFileId: companyNewsTileConfig.fileId}})
          }
          updateContent(Object.assign({}, companyNewsTileConfig, {fileId: ""}))
          dispatch({type: "UPLOAD_STATUS", payload: {uploadStatus: "", uploadName: ""}})
        }

        /** Common logic for create and edit */
        const {uuid, uploadUrl} = await createFileMutation({
          variables: {
            name: state.acceptedFile?.name.split(".").slice(0, -1).join("."),
            mimeType: state.acceptedFile?.type
          }
        })

        /** Upload to S3 */
        await axios.put(uploadUrl, formData, {
          headers: {"Content-Type": "application/pdf"}
        })
        await pollStatus(uuid)
      } catch (error) {
        console.error("Upload failed:", error)
      }
    }
  }

  return (
    <Flex direction="column" alignItems="center" gap={3}>
      <H3>
        <FormattedMessage id="culturehub.companyNews.acceptTerms.header.title" />
      </H3>
      <img
        src="https://res.cloudinary.com/perkbox/image/upload/v1738235085/e0bedb7c-8d5e-42b4-923b-67077b92cd55.png"
        alt=""
        width="200px"
        height="200px"
      />

      {showError && <Alert type={AlertTypes.error} title="culturehub.companyNews.acceptTerms.alertMsg" nonDismissable />}
      <Checkbox name="termsAndConditionsCheckbox" onChange={() => setIsChecked(!isChecked)} checked={isChecked}>
        <Label large>
          <FormattedMessage id="culturehub.companyNews.acceptTerms.checkbox.terms1" />
        </Label>
        <H4 as="span">
          <Link target="_blank" href={formatMessage({id: "culturehub.companyNews.acceptTerms.checkbox.href"})} cta large>
            <FormattedMessage id="culturehub.companyNews.acceptTerms.checkbox.terms2" />
          </Link>
        </H4>
      </Checkbox>
      <Button primary full onClick={handleChange}>
        <FormattedMessage id="culturehub.companyNews.acceptTerms.cta.title" />
      </Button>
    </Flex>
  )
}

export default AcceptTerms
